import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { INVOICE_STATUS_COLOR, InvoiceStatusEnum } from "@/lib/enum/invoice-status.enum";
import { InvoiceTypeEnum } from "@/lib/enum/invoice-type.enum";
import { getCurrencyFractionDigits } from "@/lib/utility/currency-helper";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICustomViewableEntity } from "@/lib/interfaces/custom-viewable-entity.interface";
import { IMultiSendMailEntry } from "@/lib/interfaces/template/multi-send-mail-entry.interface";
import { $t } from "@/lib/utility/t";
import { PartnerEntity } from "@/models/partnerEntity";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { UpdateDto } from "@/lib/utility/data/update-dto.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import { downloadFileViaAnchor } from "@/lib/utility/downloadFileFunc";
import { GroupGoToHelper } from "@/lib/utility/group.go-to-helper";
import { InvoiceGoToHelper } from "@/lib/utility/invoice.go-to-helper";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import invoiceService from "@/services/mrfiktiv/services/invoice-service";
import xInvoiceService from "@/services/mrfiktiv/services/x-invoice-service";
import { formatCurrency } from "@/lib/utility/currency-helper";
import {
  MrfiktivAccountingRecordItemViewmodelGen,
  MrfiktivByAtDtoGen,
  MrfiktivCreateInboundInvoiceDtoGen,
  MrfiktivCreateInvoicePaymentDtoGen,
  MrfiktivCreateOutboundInvoiceDocumentDtoGen,
  MrfiktivCreateOutboundInvoiceDtoGen,
  MrfiktivInvoiceControllerCreatePayloadGen,
  MrfiktivInvoiceControllerUpdatePayloadGen,
  MrfiktivInvoicePaymentViewModelGen,
  MrfiktivInvoicePeriodViewModelGen,
  MrfiktivInvoiceViewModelGen,
  MrfiktivUpdateInboundInvoiceDtoGen,
  MrfiktivUpdateOutboundInvoiceDtoGen,
  MrfiktivXInvoiceDataViewModelGen,
  MrfiktivXInvoiceImportReportViewModelGen,
  MrfiktivXInvoiceValidationReportViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { CompanyDataAccessLayer } from "@/store/modules/access-layers/company.access-layer";
import { FleetDataAccessLayer } from "@/store/modules/access-layers/fleet.access-layer";
import { InvoiceDataAccessLayer } from "@/store/modules/access-layers/invoice.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { VehicleAccessLayer } from "@/store/modules/access-layers/vehicle.access-layer";
import { UserModule } from "@/store/modules/me-user.store";
import VueRouter from "vue-router";
import { ActivityLog } from "./activity-log.entity";
import { Address } from "./address.entity";
import { Company, ICompany } from "./company.entity";
import { CustomFieldValue, ICustomFieldValue } from "./custom-field-value.entity";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IPrecedingInvoice, PrecedingInvoice } from "./preceding-invoice.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IReport, Report } from "./report.entity";
import { handleError } from "@/lib/utility/handleError";
import { log } from "@/lib/utility/log";
import { XInvoiceProfileEnum } from "@/lib/enum/x-invoice-profile.enum";

/**
 * The "direction" of document
 */
export enum DocumentDirectionEnum {
  /**
   * Inbound documents. Usually the partner is the `buyer` or "PURCHASE" on inbound documents.
   * "Eingangsrechnung" is typically translated as a "purchase invoice" or "supplier invoice".
   * This document is received from a vendor or supplier for goods or services purchased.
   */
  INBOUND = "inbound",

  /**
   * Outbound documents. Usually the partner is the `seller` or "SALE" on outbound documents.
   * "Ausgangsrechnung" is generally referred to as a "sales invoice" or "customer invoice".
   * This is the document that the partner issues to the customer to bill them for delivered goods or rendered services.
   */
  OUTBOUND = "outbound"
}

// moved to lib/enum so display components can use it without pulling in the
// store import chain of this entity; re-exported for existing importers
export { InvoiceTypeEnum };

/** A single recorded payment on an outbound invoice. Shape matches the backend view model as-is. */
export type IInvoicePayment = MrfiktivInvoicePaymentViewModelGen;

/**
 * Document types whose gross total must be strictly positive (`> 0`) when entered manually:
 * the document type carries the direction (a credit note pays out, an invoice charges), so the
 * total itself is unsigned. Single negative lines (discounts, deductions such as a rental car
 * deduction inside a credit note) stay allowed as long as the document total remains positive.
 * A total of 0,00 books nothing (DATEV rejects 0,00 records) and is almost certainly an input error.
 *
 * The rule is defined per document type, not globally:
 * - a corrected invoice ({@link InvoiceTypeEnum.CORRECTED}, UNTDID 1001 code 384) may legitimately
 *   carry a negative total and is therefore not listed here.
 * - it is enforced by the manual entry forms only. Automatically ingested documents (e-mail
 *   ingestion, external systems) may arrive as "negative invoices" — a valid EN 16931 way to
 *   express a credit — and must not be rejected.
 *
 * The listed types are exactly the ones the manual type selectors expose (see the `invoiceTypes`
 * getters in InvoiceFormPreview and InvoiceInboundForm). When PARTIAL or SELF_BILLED become
 * selectable there, revisit this list and decide their sign rule deliberately.
 *
 * The PDF-upload review step in CreateInvoiceInboundDialog deliberately counts as manual entry:
 * a supplier document that is a negative invoice on paper (valid EN 16931) is recorded as an
 * inbound credit note — the one-click fix in InvoiceTotalSignAlert performs exactly that mapping —
 * while the attached source document stays unchanged.
 */
export const INVOICE_TYPES_REQUIRING_POSITIVE_TOTAL: readonly InvoiceTypeEnum[] = [
  InvoiceTypeEnum.COMMERCIAL,
  InvoiceTypeEnum.CREDIT_NOTE
];

/**
 * Statuses in which an invoice is still owed: anything except draft (not yet billed) and paid
 * (nothing left to be due). Feeds the `states` prop of `due-date-chip` wherever it renders an
 * invoice's due date, so the "still open" definition lives in one place.
 */
export const INVOICE_DUE_STATUSES: readonly InvoiceStatusEnum[] = Object.values(InvoiceStatusEnum).filter(
  (status) => status !== InvoiceStatusEnum.DRAFT && status !== InvoiceStatusEnum.PAID
);

@IsFilterable
export class InvoiceBase
  implements
    IEntity<IInvoice, MrfiktivInvoiceControllerUpdatePayloadGen>,
    ICustomViewableEntity<IInvoice, MrfiktivInvoiceControllerUpdatePayloadGen>,
    Omit<MrfiktivInvoiceViewModelGen, "values" | "buyer" | "seller">,
    Partial<Pick<MrfiktivInvoiceViewModelGen, "buyer" | "seller">>,
    UpdateDto<IInvoice>
{
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.direction",
    config: {
      items: Object.values(DocumentDirectionEnum).map((v) => {
        return {
          text: `enums.DocumentDirectionEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  direction: DocumentDirectionEnum;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.type",
    config: {
      items: Object.values(InvoiceTypeEnum).map((v) => {
        return {
          text: `enums.InvoiceTypeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  type: InvoiceTypeEnum;

  /** Id of the invoice object */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.id"
  })
  id: string;

  /** Invoice number */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.number"
  })
  number: string;

  /** Invoice date */
  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.invoice.date"
  })
  get dateFormable(): string {
    if (!this.date) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.date));
  }
  set dateFormable(date: string) {
    if (date) {
      this.date = new Date(date).toISOString();
    }
  }

  date: string;

  /** Group ID */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.groupId"
  })
  groupId?: string;

  /** Currency */
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.invoice.currency"
  })
  currency: CurrencyEnum;

  /** Buyer */
  @FilterConfig({
    type: InvoiceParty
  })
  buyer?: IInvoiceParty;

  buyerCompany?: ICompany;

  /** Supplier */
  @FilterConfig({
    type: InvoiceParty
  })
  seller?: IInvoiceParty;

  sellerCompany?: ICompany;

  /** Due Date */
  get dueFormable(): string {
    if (!this.due) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.due));
  }
  set dueFormable(date: string) {
    if (date) {
      this.due = new Date(date).toISOString();
    }
  }

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.invoice.due"
  })
  due?: string;

  servicePeriod?: MrfiktivInvoicePeriodViewModelGen | undefined;

  /** Service period start date */
  get servicePeriodStart(): string {
    if (!this.servicePeriod?.start) {
      return "";
    }

    return formatYearsMonthDay(new Date(this.servicePeriod.start));
  }
  set servicePeriodStart(date: string) {
    if (date) {
      if (this.servicePeriod) {
        this.servicePeriod.start = new Date(date).toISOString();
      } else {
        this.servicePeriod = {
          start: new Date(date).toISOString()
        };
      }
    }
  }

  /** Total Amount */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.total"
  })
  total?: number;

  /** Amount Due */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountDue"
  })
  amountDue?: number;

  /** External ID */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.externalId"
  })
  externalId?: string;

  /** Header */
  header?: string;

  /** Footer */
  footer?: string;

  /** Subtotal */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.subTotal"
  })
  subTotal?: number;

  /** Tax Total */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.taxTotal"
  })
  taxTotal?: number;

  /** Amount Paid */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountPaid"
  })
  amountPaid?: number;

  /** Amount Remaining */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.amountRemaining"
  })
  amountRemaining?: number;

  /** Recorded payments, oldest first */
  payments?: IInvoicePayment[];

  /** Notes */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.notes"
  })
  notes?: string[];

  /** Notes */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.invoice.notes"
  })
  lineItems: IInvoiceLine[];

  /** Invoices this document reacts to (BG-3), e.g. the invoice a correction corrects */
  precedingInvoices: IPrecedingInvoice[];

  /** Buyer reference (BT-10), e.g. the Leitweg-ID required by XRechnung. Read-only: set by the backend, e.g. on e-invoice import. */
  buyerReference?: string;

  /** The debtor number */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.debtorAccountNumber"
  })
  debtorAccountNumber?: number;

  /** The creditor number */

  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.invoice.creditorAccountNumber"
  })
  creditorAccountNumber?: number;

  /** Document */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.documentIds",
    config: {
      itemCallback: () => SignDocumentDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-document",
      refType: BackendResourceEnum.DOCUMENT
    }
  })
  documentId?: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.invoice.isBilled"
  })
  isBilled: boolean;

  billed?: MrfiktivByAtDtoGen;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.invoice.isSent"
  })
  isSent: boolean;

  sent?: MrfiktivByAtDtoGen;

  document?: ISignDocument;

  xInvoice?: MrfiktivXInvoiceDataViewModelGen;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.reportId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-report",
      refType: BackendResourceEnum.REPORT
    }
  })
  reportId?: string;

  report?: IReport;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.fleetId",
    config: {
      itemCallback: () => FleetDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      itemText: "title",
      component: "refs-fleet",
      refType: BackendResourceEnum.FLEET
    }
  })
  fleetId?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.vehicleId",
    config: {
      itemCallback: () => VehicleAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-vehicle",
      refType: BackendResourceEnum.VEHICLE
    }
  })
  vehicleId?: string;

  partnerId: string;

  userId: string;

  @FilterConfig({
    displayName: "objects.ticket.state",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(InvoiceStatusEnum).map((e) => {
        return {
          text: `enums.InvoiceStatusEnum.${e}`,
          value: e
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    width: "85"
  })
  status: InvoiceStatusEnum;

  timestamp: ITimestamp;

  accountRecord?: MrfiktivAccountingRecordItemViewmodelGen[];

  get isUpdateable() {
    if (this.document?.isInternal) {
      return true;
    }

    return (
      !this.isBilled &&
      (this.direction === DocumentDirectionEnum.INBOUND ||
        (this.direction === DocumentDirectionEnum.OUTBOUND && this.status === InvoiceStatusEnum.DRAFT))
    );
  }

  /**
   * Whether the invoice is far enough along to be mailed to its recipient. Only drafts are
   * excluded: once an invoice is finalized (has a number, status open or later) it may be
   * (re)sent. Gates both the single-send action and the multi-send selection.
   */
  get isSendable() {
    return this.status !== InvoiceStatusEnum.DRAFT;
  }

  /**
   * Whether the invoice is still owed: finalized (not a draft) and not yet fully paid. Drives the
   * overdue (red) highlighting of the due-date-chip wherever it is shown for this invoice.
   */
  get isDue(): boolean {
    return INVOICE_DUE_STATUSES.includes(this.status);
  }

  /**
   * Whether a payment can be recorded against this invoice: an outbound, non-draft, non-voided
   * invoice with a positive outstanding balance. Mirrors the backend's assertCanReceivePayment guard.
   */
  get isPaymentEligible(): boolean {
    return (
      this.isOutbound &&
      this.status !== InvoiceStatusEnum.DRAFT &&
      this.status !== InvoiceStatusEnum.VOIDED &&
      (this.amountRemaining ?? 0) > 0
    );
  }

  /** True once at least one payment has been recorded but the invoice is not yet fully paid. */
  get isPartiallyPaid(): boolean {
    return this.isOutbound && (this.amountPaid ?? 0) > 0 && (this.amountPaid ?? 0) < (this.amountDue ?? 0);
  }

  /** Percentage of amountDue paid so far, rounded to the nearest whole percent. */
  get paidPercentage(): number {
    if (!this.amountDue) {
      return 0;
    }
    return Math.round(((this.amountPaid ?? 0) / this.amountDue) * 100);
  }

  loading = false;

  get projectId() {
    return this.groupId;
  }

  get titleReadable() {
    return `${this.number}`;
  }

  get statusColor() {
    return INVOICE_STATUS_COLOR.get(this.status);
  }

  get totalGross() {
    return this.lineItems.reduce((acc, item) => acc + (1 + item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  /**
   * The gross total formatted with the invoice currency, e.g. "1.840,00 €".
   * Exposed as template token `invoice.totalFormatted`.
   */
  get totalFormatted(): string {
    return formatCurrency(this.total ?? 0, this.currency);
  }

  /**
   * The net total formatted with the invoice currency, e.g. "1.546,22 €".
   * Exposed as template token `invoice.subTotalFormatted`.
   */
  get subTotalFormatted(): string {
    return formatCurrency(this.subTotal ?? 0, this.currency);
  }

  get totalNet() {
    return this.lineItems.reduce((acc, item) => acc + item.amount * item.quantity, 0);
  }

  get totalTax() {
    return this.lineItems.reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get taxRates() {
    return Array.from(new Set(this.lineItems.map((line) => line.taxRate))).filter((rate) => rate !== 0);
  }

  get isInbound() {
    return this.direction === DocumentDirectionEnum.INBOUND;
  }

  get isOutbound() {
    return this.direction === DocumentDirectionEnum.OUTBOUND;
  }

  get isCreditNote() {
    return this.type === InvoiceTypeEnum.CREDIT_NOTE;
  }

  /**
   * Whether we issued this document ourselves: we write what we send out and record what we
   * receive.
   */
  get isAuthoredByPartner(): boolean {
    return this.isOutbound;
  }

  /** The i18n key describing who wrote this document */
  get authorLabelKey(): string {
    return this.isAuthoredByPartner ? "invoice.author.byPartner" : "invoice.author.bySupplier";
  }

  /**
   * Whether the document type reacts to an earlier invoice and can therefore
   * carry preceding invoice references (BG-3).
   */
  get supportsPrecedingInvoices(): boolean {
    return [InvoiceTypeEnum.CREDIT_NOTE, InvoiceTypeEnum.CORRECTED].includes(this.type);
  }

  /**
   * Whether no line item carries entered content yet, i.e. the form is still on the blank default
   * line a new invoice starts with. Used to decide whether prefilling positions would overwrite
   * anything the user typed.
   */
  get isLineItemsPristine(): boolean {
    return this.lineItems.every(
      (line) => !line.title?.trim() && !line.description?.trim() && !line.amount && !line.productId
    );
  }

  /**
   * Whether a missing preceding invoice reference should be warned about. A
   * correction that names no corrected invoice is flagged by receivers and by
   * the KoSIT validator, but neither treats it as a hard error, so this is
   * advisory only.
   */
  get shouldReferencePrecedingInvoice(): boolean {
    return this.type === InvoiceTypeEnum.CORRECTED && !this.precedingInvoices.length;
  }

  /**
   * The subTotal signed by document type: credit notes reduce a sum, all other types increase it.
   * Credit notes exist in the wild with both positive and negative subTotals, so the absolute value is used.
   */
  get signedSubTotal(): number {
    if (this.isCreditNote) {
      return -Math.abs(this.subTotal || 0);
    }

    return this.subTotal || 0;
  }

  get hasXInvoiceXml(): boolean {
    return !!this.xInvoice?.xml;
  }

  /**
   * The invoice number reduced to characters that are legal in a file name on every platform.
   * Invoice numbers regularly carry separators like `RE/1` which would otherwise break the download.
   */
  get fileBaseName(): string {
    const baseName = (this.number || this.id || "invoice").trim().replace(/[\\/:*?"<>|]+/g, "_");

    return baseName || "invoice";
  }

  get xInvoiceXmlFileName(): string {
    return `${this.fileBaseName}.xml`;
  }

  /**
   * Name to fall back to when downloading the attached document that carries no name of its own.
   */
  get documentFileName(): string {
    return `${this.fileBaseName}.pdf`;
  }

  /**
   * Whether this document's type requires a strictly positive gross total on manual entry.
   * @see INVOICE_TYPES_REQUIRING_POSITIVE_TOTAL
   */
  get requiresPositiveTotal(): boolean {
    return INVOICE_TYPES_REQUIRING_POSITIVE_TOTAL.includes(this.type);
  }

  /**
   * The gross total rounded to the currency's minor unit (cents for EUR, whole units for JPY).
   * `totalGross` sums floating point line items, so a mathematically zero total (e.g. +250 and
   * -250) can leave residues like 1e-14 that would defeat a `> 0` check; amounts below the
   * currency's smallest bookable unit round to zero as well.
   */
  get totalGrossRounded(): number {
    const factor = Math.pow(10, getCurrencyFractionDigits(this.currency));
    return Math.round(this.totalGross * factor) / factor;
  }

  /**
   * Total-sign invariant for manual entry: the document type carries the direction, so the gross
   * total of the types in {@link INVOICE_TYPES_REQUIRING_POSITIVE_TOTAL} must be strictly positive.
   * Single negative lines (deductions) remain allowed as long as the total stays positive.
   */
  get isTotalSignValid(): boolean {
    if (!this.requiresPositiveTotal) {
      return true;
    }

    return this.totalGrossRounded > 0;
  }

  /**
   * Inverts the sign of every line item amount. Converts a document that was entered "mirrored"
   * (e.g. credit note amounts typed with a minus) into the canonical style where the document type
   * carries the sign: refund lines become positive, deduction lines negative.
   */
  invertLineItemAmounts(): void {
    for (const line of this.lineItems) {
      line.amount = -line.amount;
    }
  }

  values?: ICustomFieldValue[];

  refs?: IReference[];

  constructor(data?: Partial<MrfiktivInvoiceViewModelGen> | IInvoice) {
    this.id = data?.id ?? "";
    this.direction = (data?.direction as DocumentDirectionEnum) ?? DocumentDirectionEnum.INBOUND;
    this.type = (data?.type as InvoiceTypeEnum) ?? InvoiceTypeEnum.COMMERCIAL;

    if (data?.buyer) {
      this.buyer = new InvoiceParty(data?.buyer);
    }

    if (data?.seller) {
      this.seller = new InvoiceParty(data?.seller);
    }

    this.externalId = data?.externalId;
    this.groupId = data?.groupId;
    this.userId = data?.userId ?? "";
    this.values = CustomFieldValue.buildCustomFieldValues(data?.values || []);
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data?.status as InvoiceStatusEnum) ?? InvoiceStatusEnum.DRAFT;
    this.number = data?.number ?? "";
    this.date = data?.date ?? new Date().toISOString();
    this.servicePeriod = data?.servicePeriod ?? {
      start: this.date
    };
    this.currency = (data?.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.header = data?.header;
    this.lineItems = ((data?.lineItems as IInvoiceLine[]) ?? []).map((line) => new InvoiceLine(line));
    this.precedingInvoices = (data?.precedingInvoices ?? []).map((preceding) => new PrecedingInvoice(preceding));
    this.buyerReference = data?.buyerReference;
    this.footer = data?.footer;
    this.subTotal = data?.subTotal;
    this.taxTotal = data?.taxTotal;
    this.total = data?.total;
    this.amountDue = data?.amountDue ?? data?.total;
    this.amountPaid = data?.amountPaid;
    this.amountRemaining = data?.amountRemaining;
    this.payments = data?.payments ?? [];
    this.isBilled = data?.isBilled || false;
    this.billed = data?.billed;
    this.isSent = data?.isSent || false;
    this.sent = data?.sent;
    this.due = data?.due ?? new Date().toISOString();
    this.notes = data?.notes;
    this.accountRecord = data?.accountRecord;
    this.creditorAccountNumber = data?.creditorAccountNumber;
    this.debtorAccountNumber = data?.debtorAccountNumber;
    this.documentId = data?.documentId;
    this.xInvoice = data?.xInvoice;
    this.reportId = data?.reportId;
    this.report = data?.report ? new Report(data?.report) : undefined;
    this.fleetId = data?.fleetId;
    this.vehicleId = data?.vehicleId;
    this.partnerId = data?.partnerId ?? "";
    this.timestamp = new Timestamp(data?.timestamp);
  }

  map(data: Partial<MrfiktivInvoiceViewModelGen>): void {
    this.id = data.id ?? "";
    this.direction = (data?.direction as DocumentDirectionEnum) ?? DocumentDirectionEnum.INBOUND;
    this.type = (data?.type as InvoiceTypeEnum) ?? InvoiceTypeEnum.COMMERCIAL;
    this.externalId = data.externalId;
    this.groupId = data.groupId;
    this.userId = data.userId ?? "";
    this.values = CustomFieldValue.buildCustomFieldValues(data.values || []);
    this.refs = Reference.filterDuplicates((data.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data.status as InvoiceStatusEnum) ?? InvoiceStatusEnum.DRAFT;
    this.number = data.number ?? "";
    this.date = data?.date ?? new Date().toISOString();
    this.servicePeriod = data?.servicePeriod ?? {
      start: this.date
    };
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.buyer = new InvoiceParty(data.buyer);
    this.seller = new InvoiceParty(data.seller);
    this.header = data.header;
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line));
    this.precedingInvoices = (data.precedingInvoices ?? []).map((preceding) => new PrecedingInvoice(preceding));
    this.buyerReference = data.buyerReference;
    this.footer = data.footer;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.total = data.total;
    this.amountDue = data.amountDue;
    this.amountPaid = data.amountPaid;
    this.amountRemaining = data.amountRemaining;
    this.payments = data.payments ?? [];
    this.isBilled = data?.isBilled || false;
    this.billed = data?.billed;
    this.isSent = data?.isSent || false;
    this.sent = data?.sent;
    this.due = data.due ?? new Date().toISOString();
    this.notes = data.notes;
    this.accountRecord = data.accountRecord;
    this.creditorAccountNumber = data?.creditorAccountNumber;
    this.debtorAccountNumber = data?.debtorAccountNumber;
    this.documentId = data?.documentId;
    this.xInvoice = data?.xInvoice;
    this.reportId = data?.reportId;
    this.report = data.report ? new Report(data?.report) : undefined;
    this.fleetId = data?.fleetId;
    this.vehicleId = data.vehicleId;
    this.partnerId = data.partnerId ?? "";
    this.timestamp = new Timestamp(data.timestamp);
  }

  async create(): Promise<this> {
    let createInvoiceDto: MrfiktivInvoiceControllerCreatePayloadGen;

    if (!this.buyer) {
      throw Error("Buyer missing");
    }
    if (!this.seller) {
      throw Error("Seller missing");
    }

    if (this.isInbound) {
      const dto: MrfiktivCreateInboundInvoiceDtoGen = {
        number: this.number,

        direction: this.direction,
        type: this.type,
        currency: this.currency,

        buyer: this.buyer.dto,
        seller: this.seller.dto,

        lineItems: this.lineItems.map((line) => line.dto),

        precedingInvoices: this.precedingInvoices.map((preceding) => preceding.dto),

        due: this.due,
        servicePeriod: this.servicePeriod,

        footer: this.footer,
        header: this.header,
        notes: this.notes?.filter((n) => n && n.trim() !== ""),
        date: this.date,

        accountRecord: this.accountRecord,

        externalId: this.externalId,
        documentId: this.documentId,

        vehicleId: this.vehicleId || null,
        reportId: this.reportId || null,
        fleetId: this.fleetId || null,
        groupId: this.groupId || null,
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
        refs: Reference.filterDuplicates(this.refs as IReference[])
      };
      createInvoiceDto = dto;
    } else {
      const dto: MrfiktivCreateOutboundInvoiceDtoGen = {
        direction: this.direction,
        type: this.type,
        currency: this.currency,

        buyer: this.buyer.dto,
        seller: this.seller.dto,

        lineItems: this.lineItems.map((line) => line.dto),

        precedingInvoices: this.precedingInvoices.map((preceding) => preceding.dto),

        due: this.due,
        servicePeriod: this.servicePeriod ?? {
          start: this.date
        },

        footer: this.footer,
        header: this.header,
        notes: this.notes?.filter((n) => n && n.trim() !== ""),
        date: this.date,

        accountRecord: this.accountRecord,

        externalId: this.externalId,

        vehicleId: this.vehicleId || null,
        reportId: this.reportId || null,
        fleetId: this.fleetId || null,
        groupId: this.groupId || null,
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
        refs: Reference.filterDuplicates(this.refs as IReference[])
      };

      createInvoiceDto = dto;
    }

    const res = await invoiceService.create(this.partnerId, createInvoiceDto);
    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.addCreationActivities();

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await invoiceService.getOne(this.partnerId, this.id);
      this.map(res);

      if (this.documentId) {
        this.document = SignDocumentDataAccessLayer.maps.id.get(this.documentId)[0];
        if (!this.document) {
          this.document = new SignDocument({ id: this.documentId, partnerId: this.partnerId });
        }

        try {
          await this.document.fetch();
        } catch (error) {
          if (!this.hasXInvoiceXml) {
            handleError(error);
          }
          this.document = undefined;
        }
      }

      if (this.isInbound && this.seller?.ref?.refId && this.seller.ref?.refType === ResourceEnum.COMPANY) {
        this.sellerCompany = CompanyDataAccessLayer.maps.id.get(this.seller.ref.refId)[0];
        if (!this.sellerCompany) {
          this.sellerCompany = new Company({ id: this.seller.ref.refId, partnerId: this.partnerId });
        }

        await this.sellerCompany?.fetch();
      }

      if (this.isOutbound && this.buyer?.ref?.refId && this.buyer.ref?.refType === ResourceEnum.COMPANY) {
        this.buyerCompany = CompanyDataAccessLayer.maps.id.get(this.buyer.ref.refId)[0];
        if (!this.buyerCompany) {
          this.buyerCompany = new Company({ id: this.buyer.ref.refId, partnerId: this.partnerId });
        }

        await this.buyerCompany?.fetch();

        console.log("fetched buyerCompany", this.buyerCompany);
      }

      InvoiceDataAccessLayer.set(this);
    } catch (e) {
      InvoiceDataAccessLayer.delete(this);
      throw e;
    } finally {
      this.loading = false;
    }
    return this;
  }

  /**
   * @param dto the properties to update
   * @param options.skipActivityLog suppresses the "properties changed" activity, e.g. for a full
   * update where every key is in the dto and listing all of them says nothing
   */
  async updatePartial(
    dto: Partial<MrfiktivInvoiceControllerUpdatePayloadGen>,
    options: { skipActivityLog?: boolean } = {}
  ): Promise<this> {
    if (dto.values) {
      dto.values = CustomFieldValue.buildCustomFieldValuesDto((dto.values as ICustomFieldValue[]) || []);
    }

    const res = await invoiceService.update(this.partnerId, this.id, dto);
    this.map(res);
    InvoiceDataAccessLayer.set(this);

    if (!options.skipActivityLog) {
      await this.logActivity({
        actionType: ActionEnum.UPDATE,
        activity: ActivityTypeEnum.MANAGE_PROPERTIES_ON_SOURCE,
        // the timeline translates every comma separated part, so hand over i18n keys
        comment: Object.keys(dto)
          .map((key) => `objects.invoice.${key}`)
          .join(",")
      });
    }

    return this;
  }

  async update(): Promise<this> {
    if (!this.buyer) {
      throw Error("Buyer missing");
    }
    if (!this.seller) {
      throw Error("Seller missing");
    }

    this.loading = true;

    try {
      let update: MrfiktivInvoiceControllerUpdatePayloadGen;

      if (this.isInbound) {
        // An inbound invoice does not need a document: it can be entered manually via
        // "Ohne Beleg erstellen" or imported as an X-Rechnung, which only ever carries XML.
        // `documentId` is optional on the DTO and `create` never required one either.
        const dto: MrfiktivUpdateInboundInvoiceDtoGen = {
          number: this.number,

          direction: this.direction,
          type: this.type,
          currency: this.currency,

          buyer: this.buyer.dto,
          seller: this.seller.dto,

          lineItems: this.lineItems.map((line) => line.dto),

          precedingInvoices: this.precedingInvoices.map((preceding) => preceding.dto),

          due: this.due,
          footer: this.footer,
          header: this.header,
          notes: this.notes?.filter((n) => n && n.trim() !== ""),
          date: this.date,
          servicePeriod: this.servicePeriod,

          accountRecord: this.accountRecord,

          externalId: this.externalId,
          documentId: this.documentId,

          vehicleId: this.vehicleId || null,
          reportId: this.reportId || null,
          fleetId: this.fleetId || null,
          groupId: this.groupId || null,
          values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
          refs: Reference.filterDuplicates(this.refs as IReference[])
        };
        update = dto;
      } else {
        const dto: MrfiktivUpdateOutboundInvoiceDtoGen = {
          direction: this.direction,
          type: this.type,
          currency: this.currency,

          buyer: this.buyer.dto,
          seller: this.seller.dto,

          lineItems: this.lineItems.map((line) => line.dto),

          precedingInvoices: this.precedingInvoices.map((preceding) => preceding.dto),

          due: this.due,
          footer: this.footer,
          header: this.header,
          notes: this.notes?.filter((n) => n && n.trim() !== ""),
          date: this.date,
          servicePeriod: this.servicePeriod,

          accountRecord: this.accountRecord,

          externalId: this.externalId,

          vehicleId: this.vehicleId || null,
          reportId: this.reportId || null,
          fleetId: this.fleetId || null,
          groupId: this.groupId || null,
          values: CustomFieldValue.buildCustomFieldValuesDto(this.values ?? []),
          refs: Reference.filterDuplicates(this.refs as IReference[])
        };

        update = dto;
      }

      // a full update carries every key, so log it as a plain edit instead of listing all of them
      await this.updatePartial(update, { skipActivityLog: true });

      await this.logActivity({ actionType: ActionEnum.UPDATE });
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
    return this;
  }

  async delete(): Promise<void> {
    const res = await invoiceService.delete(this.partnerId, this.id);
    this.map(res);
    InvoiceDataAccessLayer.delete(this);
  }

  async print(zugferd?: boolean): Promise<this> {
    const res = await invoiceService.print(this.partnerId, this.id, zugferd);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.fetch(); // loads the document for documentId

    // printing finalizes the invoice (number, status, PDF), so it is worth its own entry
    await this.logActivity({
      actionType: ActionEnum.UPDATE,
      activity: ActivityTypeEnum.PRINT_INVOICE,
      comment: this.number,
      target: this.documentId ? [{ refType: BackendResourceEnum.DOCUMENT, refId: this.documentId }] : undefined,
      isDeletable: false
    });

    return this;
  }

  /** Validate and move this inbound draft invoice to open. */
  async open(): Promise<this> {
    if (!this.isInbound) {
      throw Error("Only inbound invoices can be opened");
    }
    if (this.status !== InvoiceStatusEnum.DRAFT) {
      throw Error("Only draft invoices can be opened");
    }
    if (!this.buyer) {
      throw Error("Buyer missing");
    }
    if (!this.seller) {
      throw Error("Seller missing");
    }

    const res = await invoiceService.open(this.partnerId, this.id);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.logActivity({ actionType: ActionEnum.UPDATE });

    return this;
  }

  /**
   * Resolves the invoice PDF to attach to a mail. Prefers the loaded document; falls
   * back to a lightweight document built from `documentId` so the attachment shows and
   * is referenced by id even when the paginated list only carries the id (the backend
   * resolves name/size from the id at send time).
   *
   * @param number the display invoice number, used for the attachment file name
   */
  private resolveMailAttachment(number: string): ISignDocument[] {
    if (this.document) return [this.document];
    if (this.documentId) {
      return [new SignDocument({ id: this.documentId, name: `${number}.pdf`, partnerId: this.partnerId })];
    }
    return [];
  }

  /**
   * Maps the outbound invoice to a multi-send mail entry: the buyer is the
   * recipient, the invoice PDF is attached, the invoice itself becomes the
   * template context (tokens `invoice.*`) and the created message is referenced
   * back to the invoice.
   *
   * @param partner the active partner, exposed as template context `partner.*`
   */
  toMailEntry(partner?: PartnerEntity): IMultiSendMailEntry {
    const number = this.number || $t("enums.InvoiceStatusEnum.draft");

    return {
      id: this.id,
      title: `${number} · ${this.subTotalFormatted}`,
      subtitle: this.buyer?.name,
      to: this.buyer?.email ? [this.buyer.email] : [],
      context: { invoice: this, partner },
      refs: [{ refType: BackendResourceEnum.INVOICE, refId: this.id }],
      attachments: this.resolveMailAttachment(number),
      prefillSubject: $t("invoice.sendMail.subject", { number }),
      // Drafts are not finalized yet and must be excluded from sending.
      disabled: !this.isSendable,
      disabledReason: this.isSendable ? undefined : "invoice.sendMail.draftNotSendable"
    };
  }

  /**
   * Marks the invoice as sent to the buyer. Re-sending preserves the first recorded
   * sent timestamp, so the original send date is kept.
   */
  async markSent(): Promise<this> {
    const res = await invoiceService.send(this.partnerId, this.id);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.logActivity({
      actionType: ActionEnum.UPDATE,
      activity: ActivityTypeEnum.SEND_INVOICE
    });

    return this;
  }

  /**
   * Records a payment against this outbound invoice and re-syncs the computed amounts/status.
   */
  async addPayment(dto: MrfiktivCreateInvoicePaymentDtoGen): Promise<this> {
    const res = await invoiceService.addPayment(this.partnerId, this.id, dto);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    const amount = formatCurrency(dto.amount, this.currency);

    await this.logActivity({
      actionType: ActionEnum.UPDATE,
      activity: ActivityTypeEnum.PAY_INVOICE,
      comment: dto.note ? `${amount} · ${dto.note}` : amount,
      isDeletable: false
    });

    return this;
  }

  async addDocument(data: MrfiktivCreateOutboundInvoiceDocumentDtoGen): Promise<this> {
    const res = await invoiceService.addDocument(this.partnerId, this.id, data);

    this.map(res);
    InvoiceDataAccessLayer.set(this);

    await this.fetch(); // loads the document for documentId

    await this.logActivity({
      actionType: ActionEnum.CREATE,
      activity: ActivityTypeEnum.CREATE_DOCUMENT,
      target: [{ refType: BackendResourceEnum.DOCUMENT, refId: data.documentId }],
      isDeletable: false
    });

    return this;
  }

  async loadPreview(zugferd?: boolean): Promise<Uint8Array> {
    return await invoiceService.generatePdfPreview(this.partnerId, this.id, zugferd);
  }

  /**
   * The reference the activities of this invoice are written on
   */
  get activitySource(): IReference {
    return { refType: BackendResourceEnum.INVOICE, refId: this.id };
  }

  /**
   * Writes an activity on this invoice.
   *
   * The activity log is documentation, never the point of the operation the user triggered, so a
   * failing log is swallowed: a printed or paid invoice must not look like it failed because the
   * activity could not be stored.
   */
  private async logActivity(data: {
    actionType: ActionEnum;
    activity?: ActivityTypeEnum;
    comment?: string;
    target?: IReference[];
    source?: IReference;
    isDeletable?: boolean;
    isUpdatable?: boolean;
    isPublic?: boolean;
  }): Promise<void> {
    try {
      await new ActivityLog({
        partnerId: this.partnerId,
        source: data.source ?? this.activitySource,
        actionType: data.actionType,
        activity: data.activity,
        comment: data.comment,
        target: data.target,
        isDeletable: data.isDeletable,
        isUpdatable: data.isUpdatable,
        isPublic: data.isPublic
      }).create();
    } catch (e) {
      log.warn("Failed to create activity log for invoice", e);
    }
  }

  /**
   * The references the document of this invoice should carry: the report and vehicle the invoice
   * is linked to plus its own refs. Uploading an invoice or credit note PDF with these refs makes
   * the document show up on the report and vehicle it belongs to instead of only hanging off the
   * invoice.
   *
   * The fleet is deliberately not part of this, even when the invoice is linked to one: fleet
   * documents are fleet-level files (contracts, insurance papers), and stamping every damage
   * invoice with a fleet ref flooded them (mmmint-ai/belmoto.schadensmeldung.digital#484). The
   * invoice itself stays linked to the fleet via its fleetId — only its document does not.
   *
   * The invoice itself is not part of this: while creating it has no id yet, and callers that
   * already hold the invoice reference (e.g. the detail view) add it themselves.
   */
  get documentRefs(): IReference[] {
    const refs: IReference[] = [...(this.refs ?? [])];

    const linked: [string | undefined, BackendResourceEnum][] = [
      [this.reportId, BackendResourceEnum.REPORT],
      [this.vehicleId, BackendResourceEnum.VEHICLE]
    ];

    for (const [refId, refType] of linked) {
      if (refId && !refs.some((ref) => ref.refType === refType && ref.refId === refId)) {
        refs.push(new Reference({ refType, refId }));
      }
    }

    return refs;
  }

  /**
   * Documents the creation of this invoice: on the invoice itself, on everything it was created
   * for (report, vehicle, fleet, document, refs) so the invoice shows up in their timelines, and
   * on the invoice a credit note credits.
   *
   * @param comment an optional comment on the creation activity, e.g. the imported file name
   */
  async addCreationActivities(comment?: string): Promise<void> {
    await this.logActivity({
      actionType: ActionEnum.CREATE,
      activity: ActivityTypeEnum.CREATE,
      comment
    });

    const sources: IReference[] = [...this.documentRefs];

    // the fleet is excluded from documentRefs so uploaded PDFs stay out of the fleet documents,
    // but the invoice itself still belongs to the fleet and shows up in its timeline
    const extras: [string | undefined, BackendResourceEnum][] = [
      [this.fleetId, BackendResourceEnum.FLEET],
      [this.documentId, BackendResourceEnum.DOCUMENT]
    ];

    for (const [refId, refType] of extras) {
      if (refId && !sources.some((source) => source.refType === refType && source.refId === refId)) {
        sources.push(new Reference({ refType, refId }));
      }
    }

    await Promise.all(
      sources.map((source) =>
        this.logActivity({
          source,
          actionType: ActionEnum.CREATE,
          activity: ActivityTypeEnum.CREATE_REF,
          target: [this.activitySource],
          isDeletable: false
        })
      )
    );

    if (!this.isCreditNote) {
      return;
    }

    // a credit note is only traceable from the invoice it credits if that invoice knows about it
    await Promise.all(
      this.precedingInvoices
        .filter((preceding) => preceding.invoiceId)
        .map((preceding) =>
          this.logActivity({
            source: { refType: BackendResourceEnum.INVOICE, refId: preceding.invoiceId as string },
            actionType: ActionEnum.CREATE,
            activity: ActivityTypeEnum.CREATE_CREDIT_NOTE,
            // a credit note is created as a draft, so it only has a number once it was printed
            comment: this.number || undefined,
            target: [this.activitySource],
            isDeletable: false
          })
        )
    );
  }

  /**
   * Imports an XRechnung XML or ZUGFeRD PDF file.
   *
   * A dry run (`options.validate`) never creates or stores anything — `report.invoice` is a
   * non-persisted preview and this returns `invoice: null` so the preview never enters
   * `InvoiceDataAccessLayer`. A real import maps `report.invoice` into a proper `Invoice` and
   * registers it, matching the `create()`/`print()` convention.
   *
   * @param partnerId
   * @param file
   * @param options.validate validate and map only: nothing is created or stored
   * @param options.force import even when the external validator rejected the dry run
   * @param options.sellerCompanyId links the imported invoice to the supplier company, the
   * invoiced seller party itself is kept as it was invoiced
   */
  static async importXInvoice(
    partnerId: string,
    file: File,
    options: { validate?: boolean; force?: boolean; sellerCompanyId?: string } = {}
  ): Promise<{ report: MrfiktivXInvoiceImportReportViewModelGen; invoice: IInvoice | null }> {
    this.assertCanUseXInvoice(ActionEnum.CREATE, partnerId);

    const report = await xInvoiceService.import(partnerId, file, options);

    if (report.dryRun || !report.invoice) {
      return { report, invoice: null };
    }

    const invoice = new this(report.invoice);
    InvoiceDataAccessLayer.set(invoice);

    await invoice.addCreationActivities(file.name);

    return { report, invoice };
  }

  /**
   * Exports this invoice as X-Invoice (EN 16931 CII) XML.
   * @param profile overrides the resolved e-invoice profile
   */
  async exportXInvoiceXml(profile?: XInvoiceProfileEnum): Promise<void> {
    this.assertCanUseXInvoice(ActionEnum.READ);

    await xInvoiceService.exportXml(this.partnerId, this.id, profile);
  }

  downloadXInvoiceXml(): void {
    const xml = this.xInvoice?.xml;
    if (!xml?.trim()) {
      return;
    }

    const fileName = this.xInvoiceXmlFileName;
    downloadFileViaAnchor(new File([xml], fileName, { type: "application/xml" }), fileName);
  }

  /**
   * Validates this invoice's X-Invoice XML against the external validator.
   * @param profile overrides the resolved e-invoice profile
   */
  async validateXInvoice(profile?: XInvoiceProfileEnum): Promise<MrfiktivXInvoiceValidationReportViewModelGen> {
    this.assertCanUseXInvoice(ActionEnum.READ);

    return await xInvoiceService.validate(this.partnerId, this.id, profile);
  }

  /**
   * Validates this invoice's stored X-Invoice XML by uploading it to the file-based validator,
   * rather than asking the backend to re-derive it from the invoice id (see `validateXInvoice`).
   * Validates exactly the XML this component already shows and offers for download.
   */
  async validateXInvoiceFile(): Promise<MrfiktivXInvoiceValidationReportViewModelGen> {
    this.assertCanUseXInvoice(ActionEnum.READ);

    const xml = this.xInvoice?.xml;
    if (!xml?.trim()) {
      throw new Error("No X-Invoice XML to validate");
    }

    const file = new File([xml], this.xInvoiceXmlFileName, { type: "application/xml" });
    return await xInvoiceService.validateFile(this.partnerId, file);
  }

  private static assertCanUseXInvoice(action: ActionEnum, partnerId: string): void {
    if (!UserModule.abilities.can(action, BackendResourceEnum.X_INVOICE, partnerId)) {
      throw new Error("Missing X-Invoice permission");
    }
  }

  private assertCanUseXInvoice(action: ActionEnum): void {
    InvoiceBase.assertCanUseXInvoice(action, this.partnerId);
  }

  /**
   * Calculates all the line items for a given taxRate
   * @param taxRate the taxRate to calculate tax for
   * @returns
   */
  calcItemsPerTaxRate(taxRate: number): number {
    return this.lineItems
      .filter((l) => l.taxRate === taxRate)
      .reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  goTo(router: VueRouter) {
    return {
      customView: (newTab = false) => {
        if (this.groupId)
          new GroupGoToHelper(router).goToGroupTypeCustomView({
            partnerId: this.partnerId,
            type: BackendResourceEnum.INVOICE,
            groupId: this.groupId,
            viewId: "0",
            newTab
          });
      },
      table: (newTab = false) =>
        new InvoiceGoToHelper(router).goToInvoiceInboundTable({
          partnerId: this.partnerId,
          newTab
        }),
      detail: (newTab = false) =>
        new InvoiceGoToHelper(router).goToInvoiceDetail({
          partnerId: this.partnerId,
          invoiceId: this.id,
          newTab
        })
    };
  }

  /**
   *
   * @param company
   */
  set sellerFromCompany(company: ICompany) {
    const invoiceParty = new InvoiceParty();

    if (!company) {
      return;
    }

    invoiceParty.name = "";
    if (company.firstName) {
      invoiceParty.name = company.firstName;
    }
    if (company.lastName) {
      if (invoiceParty.name) {
        invoiceParty.name += " ";
      }
      invoiceParty.name += company.lastName;
    }
    if (company.company) {
      if (invoiceParty.name) {
        invoiceParty.name += " ";
      }
      invoiceParty.name += company.company;
    }

    invoiceParty.email = company.contact?.email;
    invoiceParty.phone = company.contact?.phone;

    if (company.billingAddress) {
      invoiceParty.address = new Address(company.billingAddress);
    }

    invoiceParty.taxnumber = company.taxnumber;

    this.seller = invoiceParty;
    this.sellerCompany = company;
  }
}

type IInvoice = InvoiceBase;
const Invoice = Filter.createForClass(InvoiceBase);

export { IInvoice, Invoice };

/**
 * Sums invoice subTotals so that credit notes ("Gutschriften") reduce the total.
 * Use this instead of summing `subTotal` directly (e.g. for "Einnahmen"/"Ausgaben" aggregations).
 */
export function sumSignedSubTotals(invoices: Pick<IInvoice, "signedSubTotal">[]): number {
  return invoices.reduce((sum, invoice) => sum + invoice.signedSubTotal, 0);
}

/** Sums the subTotals of regular invoices (everything that is not a credit note) by their face value. */
function sumRegularSubTotals(invoices: Pick<IInvoice, "isCreditNote" | "subTotal">[]): number {
  return invoices.reduce((sum, invoice) => (invoice.isCreditNote ? sum : sum + (invoice.subTotal || 0)), 0);
}

/**
 * Sums the absolute subTotals of credit notes ("Gutschriften"). Credit notes exist in the wild with
 * both positive and negative subTotals, so the absolute value is used.
 */
function sumCreditNoteSubTotals(invoices: Pick<IInvoice, "isCreditNote" | "subTotal">[]): number {
  return invoices.reduce((sum, invoice) => (invoice.isCreditNote ? sum + Math.abs(invoice.subTotal || 0) : sum), 0);
}

/** Revenue ("Einnahmen"), expense ("Ausgaben") and their balance ("Saldo") for a set of invoices. */
export interface IInvoiceBalance {
  /** "Einnahmen": outbound invoices plus inbound credit notes. */
  revenue: number;
  /** "Ausgaben": inbound invoices plus outbound credit notes. */
  expense: number;
  /** "Saldo": revenue minus expense. */
  balance: number;
}

/**
 * Aggregates inbound and outbound invoices into revenue ("Einnahmen"), expense ("Ausgaben") and
 * their balance ("Saldo").
 *
 * An invoice's direction normally decides whether its money flows in or out, but credit notes
 * ("Gutschriften") reverse that direction: an outbound credit note pays money back to the customer
 * (an expense for us), while an inbound credit note is money a supplier returns to us (revenue).
 * Credit notes are therefore counted on the opposite side of their document direction:
 * - revenue = outbound invoices + |inbound credit notes|
 * - expense = inbound invoices  + |outbound credit notes|
 * - balance = revenue - expense
 */
export function calculateInvoiceBalance(
  inbound: Pick<IInvoice, "isCreditNote" | "subTotal">[],
  outbound: Pick<IInvoice, "isCreditNote" | "subTotal">[]
): IInvoiceBalance {
  const revenue = sumRegularSubTotals(outbound) + sumCreditNoteSubTotals(inbound);
  const expense = sumRegularSubTotals(inbound) + sumCreditNoteSubTotals(outbound);

  return { revenue, expense, balance: revenue - expense };
}
